import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import {
  AppointmentInfo,
  BookingDetails,
  Button,
  LoadingOverlay,
  ScrollToTop,
} from '@/components';
import type { Booking } from '@/models';
import { PaymentStatusBadge } from '@/pages/components/Badge';
import { BookingService, ProfileService } from '@/services';
import { useBookingStore } from '@/store';
import { useBrandStore } from '@/store/brandStore';
import { formatBookingCode, formatPrice } from '@/utils/format';

const ThankYou: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { bookingCode, storeId, downloadCalendarEvent } = useBookingStore();
  const { getBranchName, brand, getBrandName } = useBrandStore();

  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLoggedIn = ProfileService.isLoggedIn();

  const storeBookingCode = bookingCode || searchParams.get('bookingCode') || '';

  useEffect(() => {
    // Clear router history by replacing current entry (prevents back navigation to payment pages)
    navigate(location.pathname + location.search, { replace: true });

    BookingService.markDone();
  }, [navigate, location.pathname, location.search]);

  useEffect(() => {
    const loadBooking = async () => {
      if (!storeBookingCode) {
        setError(t('thankYou.noBookingCodeProvided'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Delay load to prevent late webhook arrival
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const bookingData = await BookingService.getBooking(storeBookingCode);
        setBooking(bookingData as Booking);
      } catch (err) {
        console.error('Failed to load booking:', err);
        setError(t('thankYou.failedToLoadBooking'));
      } finally {
        setLoading(false);
      }
    };

    if (storeBookingCode) {
      loadBooking();
    }
  }, [storeBookingCode, t]);

  const handleAddToCalendar = () => {
    if (!booking) return;

    const branchName = brand && storeId ? getBranchName(storeId) : '';

    downloadCalendarEvent({
      brandName: getBrandName() || t('hero.shopName'),
      branchName,
      translations: {
        bookingCodeLabel: t('thankYou.bookingCode'),
        customer: t('payment.customer'),
        yourAppointment: t('thankYou.yourAppointment'),
        guests: t('dateTime.guests'),
        treatmentFallback: t('components.bookingDetails.treatmentFallback', {
          id: '{{id}}',
        }),
        minutesShort: t('components.bookingDetails.minutesShort'),
      },
    });
  };

  const handleNewBooking = () => {
    navigate('/booking');
  };

  const promotionDiscount = booking?.promotionDiscount ?? 0;
  const packageDiscount = booking?.packageDiscount ?? 0;
  const depositTotal = booking?.depositTotal ?? 0;
  const total = booking?.total ?? 0;
  const payments = booking?.payments ?? [];

  if (!loading && (error || !booking)) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 pb-6">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
            <span className="text-3xl text-red-600">✗</span>
          </div>
          <h1 className="mb-2 text-xl font-semibold text-red-600">
            {t('common.error')}
          </h1>
          <p className="text-secondary-text mb-4">
            {error || t('thankYou.bookingNotFound')}
          </p>
          <Button onClick={handleNewBooking} variant="outline">
            {t('thankYou.makeAnotherBooking')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="md:bg-bg-selected flex min-h-screen flex-col items-center px-4 pb-6">
      <ScrollToTop />
      <div className="flex w-full max-w-xl flex-col items-center gap-6 pt-9 md:mt-12 md:mb-20 md:rounded-2xl md:bg-white md:p-6">
        {/* Success Header */}
        <div className="text-primary-text w-full text-center font-semibold tracking-[0.08px]">
          <p className="text-2xl leading-5">{t('thankYou.title')}</p>
          <p className="mt-4 leading-5">
            <span>{t('thankYou.bookingCodeLabel')} </span>
            <span className="text-primary">
              {formatBookingCode(storeBookingCode)}
            </span>
          </p>
        </div>

        {/* Thank You Illustration (replaces PlaceholderIllustration) */}
        <img
          src="/illustrations/thank-you.png"
          alt="Thank you illustration"
          className="mx-auto h-auto w-[195px] select-none"
          draggable={false}
        />

        {/* Additional Info Text */}
        <div className="text-primary-text text-center leading-5">
          <p className="mb-0">{t('thankYou.checkEmailLine1')}</p>
          <p>{t('thankYou.checkEmailLine2')}</p>
        </div>

        {/* Add to Calendar Button */}
        <Button
          onClick={handleAddToCalendar}
          className="bg-primary hidden h-12 w-full rounded-lg leading-7 font-semibold text-white"
        >
          {t('thankYou.addToCalendar')}
        </Button>

        {/* Appointment Info */}
        <AppointmentInfo className="w-full" />

        {/* Booking Details */}
        <BookingDetails className="w-full" />

        {/* Payment Information */}
        <div className="border-divider w-full rounded-lg border bg-white pt-4">
          <div className="mb-2 flex items-center justify-between px-4">
            <h2 className="text-primary-text leading-5 font-semibold tracking-[0.08px]">
              {t('thankYou.paymentInformation.title')}
            </h2>
            <PaymentStatusBadge status={booking?.paymentStatus} />
          </div>

          <div className="space-y-2 px-4 pb-4">
            <div className="text-primary-text flex items-center justify-between text-base leading-6 tracking-[0.08px]">
              <span>{t('thankYou.paymentInformation.subtotal')}</span>
              <span>{formatPrice(booking?.subtotal)}</span>
            </div>
            {promotionDiscount > 0 && (
              <div className="text-primary-text flex items-center justify-between text-base leading-6 tracking-[0.08px]">
                <span>
                  {t('thankYou.paymentInformation.promotionDiscount')}
                </span>
                <span className="text-error">
                  - {formatPrice(promotionDiscount)}
                </span>
              </div>
            )}
            {packageDiscount > 0 && (
              <div className="text-primary-text flex items-center justify-between text-base leading-6 tracking-[0.08px]">
                <span>{t('thankYou.paymentInformation.payWithPackage')}</span>
                <span>- {formatPrice(packageDiscount)}</span>
              </div>
            )}
            <div className="text-primary-text flex items-center justify-between text-base leading-6 font-semibold tracking-[0.08px]">
              <span>{t('thankYou.paymentInformation.total')}</span>
              <span>{formatPrice(total)}</span>
            </div>
            {depositTotal > 0 && depositTotal < total && (
              <>
                <div className="border-divider border border-dashed" />
                <div className="text-primary-text flex items-center justify-between text-base leading-6 font-semibold tracking-[0.08px]">
                  <span>{t('thankYou.paymentInformation.deposit')}</span>
                  <span>- {formatPrice(depositTotal)}</span>
                </div>
              </>
            )}
          </div>

          <div className="border-divider border-t" />

          {!!payments.length && (
            <div className="space-y-2 px-4 py-2">
              <div className="text-primary-text flex items-center justify-between text-base leading-6 tracking-[0.08px]">
                <span>{t('thankYou.paymentInformation.paymentMethod')}</span>
                <span>{payments[0].method}</span>
              </div>
              <div className="text-primary-text flex items-center justify-between text-base leading-6 tracking-[0.08px]">
                <span>{t('thankYou.paymentInformation.paymentTime')}</span>
                <span>
                  {dayjs(payments[0].paidAt).format('DD/MM/YYYY HH:mm')}
                </span>
              </div>
            </div>
          )}

          {(booking?.remainingTotal ?? 0) > 0 && (
            <div className="border-divider border-t px-4 py-2">
              <div className="text-primary-text flex items-center justify-between text-base leading-6 tracking-[0.08px]">
                <span>{t('thankYou.paymentInformation.remainingAtStore')}</span>
                <span>{formatPrice(booking?.remainingTotal)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer CTAs */}
        <div className="flex w-full items-center gap-2">
          {isLoggedIn && (
            <div className="flex-1">
              <Button
                onClick={() => navigate('/profile')}
                variant="secondary"
                className="h-12"
              >
                {t('thankYou.backToProfile')}
              </Button>
            </div>
          )}

          <div className={isLoggedIn ? 'flex-1' : 'w-full'}>
            <Button onClick={handleNewBooking} className="h-12">
              {t('thankYou.continueBooking')}
            </Button>
          </div>
        </div>
      </div>
      <LoadingOverlay isVisible={loading} />
    </div>
  );
};

export default ThankYou;
